import { useState, useEffect, ChangeEvent } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { DefaultSelect } from 'pages/calculator/MediaPlanning/common/DefaultSelect/DefaultSelect';
import WeeklyDatePicker from 'pages/calculator/MediaPlanning/common/WeeklyDatePicker/WeeklyDatePicker';
import usePlanStore from 'store/planStore';
import useTargetGroups from 'pages/calculator/MediaPlanning/Plan/Countries/hooks/useTargetGroups';
import useCampaignScenario from 'pages/calculator/MediaPlanning/Plan/Countries/hooks/useCampaignScenario';
import useSegments from 'pages/calculator/MediaPlanning/Plan/Countries/hooks/useSegments';
import CountrySetupButtonGroup from 'pages/calculator/MediaPlanning/Plan/Countries/CountryPanel/CountrySetupButtonGroup/CountrySetupButtonGroup';
import useCampaignLength from '../hooks/useCampaignLength';
import RecalculateTouchpointsDialog from './RecalculateTouchpointsDialog/RecalculateTouchpointsDialog';
import MultiSelectAutocomplete from '../../../common/MultiSelectAutocomplete/MultiSelectAutocomplete';
import HelperTooltip from '../../../../../../shared/HelperTooltip/HelperTooltip';
import linksConfig from '../../../../../../config/linking';

interface CountryPanelProps {
  country: string;
  setIsClearFormDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function CountryPanel({
  country,
  setIsClearFormDialogOpen,
}: CountryPanelProps) {
  const [
    isOpenRecalculateTouchpointsModal,
    setIsOpenRecalculateTouchpointsModal,
  ] = useState(false);
  const { t } = useTranslation();
  const {
    countryForms,
    savedCountryForms,
    selectedCountry,
    validationErrors,
    updateCountryForm,
    validateCountryForm,
    resetCountryFormConfig,
    saveCountryFormState,
    resetCalculatedBudget,
    resetSavedCalculatedBudget,
    resetStoredCountryForm,
    setActiveNestedTab,
    resetTVConfig,
    resetOOHConfig,
    setCountryOneReach,
  } = usePlanStore();

  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const countryForm = countryForms[selectedCountry!];

  const formData = countryForms[country] || {
    targetGroup: '',
    campaignScenario: '',
    segments: [],
    startDate: '',
    campaignLength: '',
  };

  const savedData = savedCountryForms[country] || {
    targetGroup: '',
    campaignScenario: '',
    segment: '',
    startDate: '',
    campaignLength: '',
  };

  const { data: targetGroups, isFetching: isFetchingTargetGroups } =
    useTargetGroups(country);

  const { data: campaignScenarios, isFetching: isFetchingCampaignScenarios } =
    useCampaignScenario(country);

  const { data: segments, isFetching: isFetchingSegments } =
    useSegments(country);

  const { data: campaignLengths, isFetching: isFetchingCampaignLengths } =
    useCampaignLength(country);

  const handleInputChange = async (
    field: string,
    value: string | number | undefined | { id: string; title: string }
  ) => {
    const updatedData = { ...formData, [field]: value || '' };

    updateCountryForm(country, updatedData);
    validateCountryForm(country, updatedData);
  };

  const handleChangeStateValues = (
    key: string,
    value: { id: string; title: string }[]
  ) => {
    updateCountryForm(country, { [key]: value });
  };

  const handleRecalculateTouchpoints = () => {
    resetCountryFormConfig(country);
    resetCalculatedBudget(country);
    resetStoredCountryForm(country);
    resetSavedCalculatedBudget(country);
    setCountryOneReach(country, undefined);
    resetTVConfig(country);
    resetOOHConfig(country);
    saveCountryFormState(country);
    setActiveNestedTab('digital');
    setIsOpenRecalculateTouchpointsModal(false);
  };

  useEffect(() => {
    const errors = validationErrors[country] || {};
    setLocalErrors(errors);
  }, [country, validationErrors]);

  const isProceedDisabled =
    !!Object.keys(localErrors).length ||
    !formData.targetGroup ||
    !formData.campaignLength ||
    !formData.startDate ||
    !formData.campaignScenario ||
    !formData.segments ||
    (formData.targetGroup === savedData.targetGroup &&
      formData.campaignLength === savedData.campaignLength &&
      formData.startDate === savedData.startDate &&
      formData.campaignScenario === savedData.campaignScenario &&
      formData.segments === savedData.segments);

  const handleCLoseRecalculateTouchpointsModal = () => {
    setIsOpenRecalculateTouchpointsModal(false);
  };

  return (
    <>
      <Accordion
        disableGutters
        defaultExpanded
        sx={{
          mb: '16px',
          '& .MuiAccordionSummary-root': {
            backgroundColor: 'common.white',
            alignItems: 'center',
            height: '48px',
            borderBottom: '1px solid #C7D8F9',
            borderTop: '1px solid #C7D8F9',
            pl: 0,
            '& .MuiAccordionSummary-content': {
              textAlign: 'center',
              height: 'initial',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              mr: 0,
            },
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            variant="h6"
            color="#525F81"
            fontWeight={700}
            fontSize="16px"
            lineHeight="3"
            textTransform="uppercase"
          >
            {t('mediaPlanning.countrySetup')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ mt: '46px', pb: '32px' }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', rowGap: '24px' }}
          >
            <Grid container>
              <Grid item xs={3} sx={{ pr: '24px' }}>
                <DefaultSelect
                  value={formData.targetGroup}
                  onChange={(newValue) =>
                    handleInputChange('targetGroup', newValue.value)
                  }
                  items={targetGroups || []}
                  selectName={t('mediaPlanning.targetGroup')}
                  labelId="target-group"
                  disabled={
                    isFetchingTargetGroups ||
                    countryForm.metaConfig?.reservationStatus === 'reserved' ||
                    countryForm.metaConfig?.reservationStatus ===
                      'partially-reserved'
                  }
                  isLoading={isFetchingTargetGroups}
                />
              </Grid>
              <Grid
                item
                xs={3}
                sx={{ pr: '24px', display: 'flex', alignItems: 'flex-start' }}
              >
                <DefaultSelect
                  value={formData.campaignScenario}
                  onChange={(newValue) =>
                    handleInputChange('campaignScenario', newValue.value)
                  }
                  items={campaignScenarios || []}
                  selectName={t('mediaPlanning.campaignScenario')}
                  labelId="campaign-scenario"
                  disabled={
                    isFetchingCampaignScenarios ||
                    !countryForms[country]?.targetGroup ||
                    countryForm.metaConfig?.reservationStatus === 'reserved' ||
                    countryForm.metaConfig?.reservationStatus ===
                      'partially-reserved'
                  }
                  isLoading={isFetchingCampaignScenarios}
                />
                <HelperTooltip
                  tooltipData={linksConfig.fullMediaScenariosDefinition}
                />
              </Grid>
              <Grid
                item
                xs={3}
                sx={{ pr: '24px', display: 'flex', alignItems: 'flex-start' }}
              >
                <MultiSelectAutocomplete
                  options={segments.map((segment) => ({
                    id: segment?.value,
                    title: segment?.name,
                  }))}
                  id="partnersMultiSelect"
                  label={t('mediaPlanning.segment')}
                  noOptionsText={t('mediaPlanning.noPartners')}
                  onChange={(
                    _: ChangeEvent,
                    newValue: { id: string; title: string }[]
                  ) => {
                    handleChangeStateValues('segments', newValue);
                  }}
                  value={formData.segments || []}
                  limitTags={1}
                  disableClearable
                  styles={{ width: '100%' }}
                  disabled={
                    isFetchingSegments ||
                    !countryForms[country]?.campaignScenario ||
                    countryForm.metaConfig?.reservationStatus === 'reserved' ||
                    countryForm.metaConfig?.reservationStatus ===
                      'partially-reserved'
                  }
                  isLoading={isFetchingSegments}
                />
                <HelperTooltip
                  tooltipData={linksConfig.sociovisionSegmentsDefinition}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={3} sx={{ pr: '24px' }}>
                <WeeklyDatePicker
                  label={t('mediaPlanning.startDate')}
                  value={formData.startDate || undefined}
                  setState={(value) => handleInputChange('startDate', value)}
                  disabled={
                    countryForm.metaConfig?.reservationStatus === 'reserved' ||
                    countryForm.metaConfig?.reservationStatus ===
                      'partially-reserved'
                  }
                />
              </Grid>
              <Grid item xs={3} sx={{ pr: '24px' }}>
                <DefaultSelect
                  value={formData.campaignLength}
                  onChange={(newValue) =>
                    handleInputChange('campaignLength', newValue.value)
                  }
                  items={campaignLengths || []}
                  selectName={t('mediaPlanning.campaignLength')}
                  labelId="campaignLength"
                  disabled={
                    isFetchingCampaignLengths ||
                    !countryForms[country]?.campaignScenario ||
                    countryForm.metaConfig?.reservationStatus === 'reserved' ||
                    countryForm.metaConfig?.reservationStatus ===
                      'partially-reserved'
                  }
                  isLoading={isFetchingCampaignLengths}
                />
              </Grid>
            </Grid>
            <Grid container>
              <CountrySetupButtonGroup
                country={country}
                toggleResetDialog={setIsClearFormDialogOpen}
                isProceedDisabled={isProceedDisabled}
                setIsOpenRecalculateTouchpointsModal={
                  setIsOpenRecalculateTouchpointsModal
                }
              />
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
      <RecalculateTouchpointsDialog
        open={isOpenRecalculateTouchpointsModal}
        onClose={handleCLoseRecalculateTouchpointsModal}
        onSuccess={handleRecalculateTouchpoints}
      />
    </>
  );
}
